import { useState, useRef, useCallback, useEffect } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { uploadService } from '@/services/uploadService';
import type { ProcessingStatus } from '@/services/uploadService';
import { closeUploadModal, showToast, setUploadInProgress } from '@/features/ui/uiSlice';
import Button from '@/components/ui/Button/Button';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import styles from './UploadModal.module.css';

// ─── Types ─────────────────────────────────────────────────────────────────

type UiView = 'drop' | 'meta' | 'queue';
type ContentType = 'media' | 'document';
type FileStatus = 'pending' | 'uploading' | 'completing' | 'processing' | 'done' | 'error';

interface FileEntry {
  id: string;
  file: File;
  title: string;
  contentType: ContentType;
  tags: string;
  description: string;
  // runtime state
  status: FileStatus;
  progress: number;
  chunksUploaded: number;
  totalChunks: number;
  processingStatus: ProcessingStatus;
  error: string | null;
}

// ─── Constants ─────────────────────────────────────────────────────────────

const FILE_EXTENSIONS: Record<string, string> = {
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WEBP',
  'image/heic': 'HEIC',
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
};

const ACCEPT_ATTR = 'video/mp4,video/quicktime,.mov,.MOV,.mp4,image/jpeg,image/png,image/gif,image/webp,image/heic,.jpg,.jpeg,.png,.gif,.webp,.heic';

const VIEW_STEPS = ['Select', 'Details', 'Processing'];
const CHUNK_MAX_ATTEMPTS    = 5;
const CHUNK_CONCURRENCY     = 4;
const STALL_TIMEOUT_MS      = 30_000;
const MAX_INITIATE_RETRIES  = 3;
const INITIATE_RETRY_DELAY_MS = 10_000;
const SESSION_TTL_MS        = 2 * 60 * 60 * 1000; // 2 hours

const PROCESSING_LABELS: Record<NonNullable<ProcessingStatus>, string> = {
  pending_cu:    'Queued for AI...',
  cu_processing: 'AI analysing...',
  cu_done:       'Building index...',
  indexed:       'Indexed!',
  error:         'Failed',
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function processingToProgress(s: ProcessingStatus): number {
  switch (s) {
    case 'pending_cu':    return 74;
    case 'cu_processing': return 82;
    case 'cu_done':       return 94;
    case 'indexed':       return 100;
    default:              return 71;
  }
}

function getContentType(file: File): ContentType {
  return file.type.startsWith('video/') || file.type.startsWith('image/') ? 'media' : 'document';
}

const VIDEO_EXTENSIONS = ['mp4', 'mov'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'];

function isVideoFile(file: File): boolean {
  if (file.type.startsWith('video/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return !!ext && VIDEO_EXTENSIONS.includes(ext);
}

function isImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return !!ext && IMAGE_EXTENSIONS.includes(ext);
}

function isSupportedFile(file: File): boolean {
  return isVideoFile(file) || isImageFile(file);
}

function getExt(file: File): string {
  return FILE_EXTENSIONS[file.type] ?? file.name.split('.').pop()?.toUpperCase() ?? 'FILE';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

// Resolves after `ms`, or rejects with an AbortError if `signal` aborts first.
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    if (signal) {
      if (signal.aborted) onAbort();
      else signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

function makeFileEntry(file: File): FileEntry {
  return {
    id:               uid(),
    file,
    title:            file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '),
    contentType:      getContentType(file),
    tags:             '',
    description:      '',
    status:           'pending',
    progress:         0,
    chunksUploaded:   0,
    totalChunks:      0,
    processingStatus: null,
    error:            null,
  };
}

function splitFileName(name: string): [string, string] {
  const idx = name.lastIndexOf('.');
  if (idx <= 0) return [name, ''];
  return [name.slice(0, idx), name.slice(idx)];
}

// Appends " (1)", " (2)", ... before the extension until the name is unique.
function uniqueFileName(name: string, existing: Set<string>): string {
  if (!existing.has(name.toLowerCase())) return name;
  const [base, ext] = splitFileName(name);
  let i = 1;
  let candidate = `${base} (${i})${ext}`;
  while (existing.has(candidate.toLowerCase())) {
    i++;
    candidate = `${base} (${i})${ext}`;
  }
  return candidate;
}

function renameFile(file: File, newName: string): File {
  return new File([file], newName, { type: file.type, lastModified: file.lastModified });
}

// ─── Upload helpers ─────────────────────────────────────────────────────────

function chunkRetryDelay(attempt: number): number {
  return Math.min(1000 * Math.pow(2, attempt - 1), 16_000) * (0.5 + Math.random() * 0.5);
}

function isRetryableChunkError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } }).response?.status;
  if (status != null) return status === 408 || status === 429 || status >= 500;
  return true; // network errors, stall aborts, etc.
}

// ─── Session persistence (W4) ───────────────────────────────────────────────

interface StoredSession {
  sessionId: string;
  chunkSize: number;
  numChunks: number;
  ts: number;
}

function sessionKey(file: File): string {
  return `upl:${file.name}:${file.size}:${file.lastModified}`;
}

function loadStoredSession(file: File): StoredSession | null {
  try {
    const raw = localStorage.getItem(sessionKey(file));
    if (!raw) return null;
    const s: StoredSession = JSON.parse(raw);
    if (Date.now() - s.ts > SESSION_TTL_MS) {
      localStorage.removeItem(sessionKey(file));
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

function saveStoredSession(file: File, s: StoredSession): void {
  try { localStorage.setItem(sessionKey(file), JSON.stringify(s)); } catch { /* quota */ }
}

function clearStoredSession(file: File): void {
  localStorage.removeItem(sessionKey(file));
}

interface DuplicateConflict {
  id: string;
  file: File;
}

// ─── Component ─────────────────────────────────────────────────────────────

export default function UploadModal() {
  const dispatch = useAppDispatch();

  const [view, setView] = useState<UiView>('drop');
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [duplicateQueue, setDuplicateQueue] = useState<DuplicateConflict[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRefs    = useRef<Map<string, AbortController>>(new Map());
  const pollRefs     = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // prevents double-triggering when the queue effect fires multiple times
  const startedIds   = useRef<Set<string>>(new Set());

  // ── Patch a single entry ────────────────────────────────────────────────
  const patchEntry = useCallback((id: string, patch: Partial<FileEntry>) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  }, []);

  // ── Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      abortRefs.current.forEach(c => c.abort());
      pollRefs.current.forEach(t => clearTimeout(t));
    };
  }, []);

  // ── Lock background scroll while the modal is open ──────────────────────
  useEffect(() => {
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // ── Close handler ───────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    abortRefs.current.forEach(c => c.abort());
    pollRefs.current.forEach(t => clearTimeout(t));
    dispatch(closeUploadModal());
  }, [dispatch]);

  // ── ESC closes (only outside the active queue) ──────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && view !== 'queue') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose, view]);

  // ── Auto-close when all entries are removed (manual cancel) ──────────────
  useEffect(() => {
    if (view !== 'queue' || entries.length > 0) return;
    dispatch(setUploadInProgress(false));
    dispatch(closeUploadModal());
  }, [view, entries, dispatch]);

  // ── Auto-close when every file is done / errored ────────────────────────
  useEffect(() => {
    if (view !== 'queue' || entries.length === 0) return;
    const allDone = entries.every(e => e.status === 'done' || e.status === 'error');
    if (!allDone) return;
    const doneCount = entries.filter(e => e.status === 'done').length;
    const total = entries.length;
    dispatch(setUploadInProgress(false));
    // If any file errored, leave the modal open so the user can read the
    // error message instead of auto-dismissing it.
    if (doneCount < total) return;
    const t = setTimeout(() => {
      dispatch(closeUploadModal());
      dispatch(
        showToast({
          title: 'Upload Complete',
          message: `<b style="color:var(--color-text)">${doneCount} of ${total}</b> file${total !== 1 ? 's' : ''} indexed and searchable in Smart Hub.`,
        }),
      );
    }, 1200);
    return () => clearTimeout(t);
  }, [entries, view, dispatch]);

  // ── Real API polling ────────────────────────────────────────────────────
  const pollStatus = useCallback((id: string, sessionId: string, title: string) => {
    const tick = async () => {
      try {
        const res = await uploadService.getStatus(sessionId);
        const status = res.processing_status;
        patchEntry(id, { processingStatus: status, progress: processingToProgress(status) });
        if (status === 'indexed') {
          trackEvent(ANALYTICS_EVENTS.UPLOAD_COMPLETE, { title, mock: false });
          patchEntry(id, { status: 'done', progress: 100 });
          dispatch(
            showToast({
              title: 'Content Indexed',
              message: `<b style="color:var(--color-text)">${title}</b> has been processed and is now searchable across Smart Hub.`,
            })
          );
          return;
        }
        if (status === 'error') {
          trackEvent(ANALYTICS_EVENTS.UPLOAD_FAIL, { title, reason: res.error_message });
          patchEntry(id, { status: 'error', error: res.error_message ?? 'Processing failed. Please try again.' });
          return;
        }
        pollRefs.current.set(`api-${id}`, setTimeout(tick, 5000));
      } catch {
        patchEntry(id, { status: 'error', error: 'Lost connection while processing. Please try again.' });
      }
    };
    pollRefs.current.set(`api-${id}`, setTimeout(tick, 3000));
  }, [patchEntry, dispatch]);

  // ── Upload a single file (no recursion — queue effect drives sequencing) ─
  const doUpload = useCallback(async (entry: FileEntry) => {
    if (startedIds.current.has(entry.id)) return;
    startedIds.current.add(entry.id);

    const { id } = entry;
    const controller = new AbortController();
    abortRefs.current.set(id, controller);

    patchEntry(id, { status: 'uploading' });
    trackEvent(ANALYTICS_EVENTS.UPLOAD_START, { title: entry.title, contentType: entry.contentType, sizeBytes: entry.file.size });

    try {
      // 1. Session: resume from localStorage or initiate fresh ──────────────
      let sessionId      = '';
      let chunkSize      = 0;
      let numChunks      = 0;
      let chunksToUpload: number[] = [];
      let alreadyUploaded = 0;

      const stored = loadStoredSession(entry.file);
      let resumed = false;
      if (stored) {
        try {
          const info = await uploadService.getChunks(stored.sessionId);
          sessionId      = stored.sessionId;
          chunkSize      = stored.chunkSize;
          numChunks      = stored.numChunks;
          chunksToUpload = info.missing;
          alreadyUploaded = info.received.length;
          resumed = true;
          console.log(`[UploadModal] resuming session ${sessionId}: ${chunksToUpload.length} missing`);
        } catch {
          clearStoredSession(entry.file);
        }
      }

      if (!resumed) {
        let init: Awaited<ReturnType<typeof uploadService.initiate>> | undefined;
        for (let attempt = 1; ; attempt++) {
          try {
            init = await uploadService.initiate({
              file_name:       entry.file.name,
              file_size_bytes: entry.file.size,
              mime_type:       entry.file.type || 'application/octet-stream',
            });
            break;
          } catch (err: unknown) {
            const errName = (err as { name?: string }).name;
            if (errName === 'AbortError' || errName === 'CanceledError') return;
            if (attempt >= MAX_INITIATE_RETRIES) {
              patchEntry(id, { status: 'error', progress: 0, error: 'Could not start upload. Please check your internet connection and try again.' });
              return;
            }
            console.warn(`[UploadModal] initiate failed (attempt ${attempt}/${MAX_INITIATE_RETRIES}), retrying`, err);
            await delay(INITIATE_RETRY_DELAY_MS, controller.signal);
          }
        }
        sessionId      = init!.session_id;
        chunkSize      = init!.chunk_size_bytes;
        numChunks      = init!.total_chunks;
        chunksToUpload = Array.from({ length: numChunks }, (_, i) => i);
        alreadyUploaded = 0;
        saveStoredSession(entry.file, { sessionId, chunkSize, numChunks, ts: Date.now() });
        console.log(`[UploadModal] session ${sessionId}: ${numChunks} chunk(s) x ${chunkSize} B`);
      }

      patchEntry(id, { totalChunks: numChunks });

      // 2. Upload chunks — parallel pool (W5) ───────────────────────────────
      let completedBytes = alreadyUploaded * chunkSize;
      const inflightLoaded = new Map<number, number>();
      let chunksCompleted = alreadyUploaded;

      const updateProgress = () => {
        const inflight = Array.from(inflightLoaded.values()).reduce((a, b) => a + b, 0);
        patchEntry(id, {
          chunksUploaded: chunksCompleted,
          progress: Math.min(Math.round(((completedBytes + inflight) / entry.file.size) * 68), 68),
        });
      };

      const uploadOneChunk = async (i: number): Promise<void> => {
        const chunk = entry.file.slice(i * chunkSize, (i + 1) * chunkSize);

        for (let attempt = 1; attempt <= CHUNK_MAX_ATTEMPTS; attempt++) {
          if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');

          // Per-attempt abort controller: handles stall + forwards user cancel
          const attemptCtrl = new AbortController();
          let lastTick = Date.now();
          let watchdogPaused = document.hidden;

          const onVisibility = () => {
            if (document.hidden) {
              watchdogPaused = true;
            } else {
              watchdogPaused = false;
              lastTick = Date.now();
            }
          };
          document.addEventListener('visibilitychange', onVisibility);
          const forwardCancel = () => attemptCtrl.abort();
          controller.signal.addEventListener('abort', forwardCancel, { once: true });

          const watchdog = setInterval(() => {
            if (!watchdogPaused && !attemptCtrl.signal.aborted && Date.now() - lastTick > STALL_TIMEOUT_MS) {
              console.warn(`[UploadModal] chunk ${i} stalled, aborting attempt ${attempt}`);
              attemptCtrl.abort();
            }
          }, 2000);

          inflightLoaded.set(i, 0);

          try {
            await uploadService.uploadChunk(sessionId, i, chunk, attemptCtrl.signal, (loaded) => {
              lastTick = Date.now();
              inflightLoaded.set(i, loaded);
              updateProgress();
            });
            clearInterval(watchdog);
            document.removeEventListener('visibilitychange', onVisibility);
            controller.signal.removeEventListener('abort', forwardCancel);
            inflightLoaded.delete(i);
            completedBytes += chunk.size;
            chunksCompleted++;
            updateProgress();
            return;
          } catch (err: unknown) {
            clearInterval(watchdog);
            document.removeEventListener('visibilitychange', onVisibility);
            controller.signal.removeEventListener('abort', forwardCancel);
            inflightLoaded.delete(i);

            const errName = (err as { name?: string }).name;
            if (errName === 'AbortError' || errName === 'CanceledError') {
              if (controller.signal.aborted) throw err; // user cancelled
              // else stall-abort — fall through to retry
            } else if (!isRetryableChunkError(err)) {
              throw err; // non-retryable 4xx
            }

            if (attempt >= CHUNK_MAX_ATTEMPTS) throw new Error(`chunk ${i} failed after ${CHUNK_MAX_ATTEMPTS} attempts`);
            const retryMs = chunkRetryDelay(attempt);
            console.warn(`[UploadModal] chunk ${i} failed (attempt ${attempt}/${CHUNK_MAX_ATTEMPTS}), retrying in ${Math.round(retryMs / 1000)}s`, err);
            await delay(retryMs, controller.signal);
          }
        }
      };

      let nextIdx = 0;
      let poolFailed = false;
      const worker = async (): Promise<void> => {
        while (!poolFailed && !controller.signal.aborted) {
          const myIdx = nextIdx;
          if (myIdx >= chunksToUpload.length) break;
          nextIdx = myIdx + 1;
          await uploadOneChunk(chunksToUpload[myIdx]);
        }
      };

      try {
        await Promise.all(Array.from({ length: Math.min(CHUNK_CONCURRENCY, Math.max(1, chunksToUpload.length)) }, worker));
      } catch (err: unknown) {
        poolFailed = true;
        const errName = (err as { name?: string }).name;
        if (errName === 'AbortError' || errName === 'CanceledError') throw err;
        trackEvent(ANALYTICS_EVENTS.UPLOAD_FAIL, { title: entry.title, reason: String(err) });
        patchEntry(id, { status: 'error', error: 'Upload failed. Please check your connection and try again.' });
        return;
      }

      if (controller.signal.aborted) return;

      // 3. Complete ──────────────────────────────────────────────────────────
      patchEntry(id, { status: 'completing', progress: 70 });

      try {
        await uploadService.complete(sessionId, {
          description: entry.description || undefined,
          tags: entry.tags ? entry.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        });
        clearStoredSession(entry.file);
      } catch (err: unknown) {
        const axErr = err as { response?: { status?: number; data?: { detail?: string } } };
        if (axErr.response?.status === 409) {
          const detail = axErr.response.data?.detail ?? '';
          if (!detail.toLowerCase().includes('missing chunks')) {
            // Already completed (e.g. duplicate complete) — treat as success
            clearStoredSession(entry.file);
          } else {
            patchEntry(id, { status: 'error', progress: 0, error: 'Upload incomplete. Please retry to finish.' });
            return;
          }
        } else {
          throw err;
        }
      }

      // 4. Hand off to AI processing ─────────────────────────────────────────
      patchEntry(id, { status: 'processing', progress: 71, processingStatus: 'pending_cu' });
      pollStatus(id, sessionId, entry.title);

    } catch (err: unknown) {
      const name = (err as { name?: string }).name;
      if (name === 'AbortError' || name === 'CanceledError') return;
      console.error('[UploadModal] upload failed:', err);
      trackEvent(ANALYTICS_EVENTS.UPLOAD_FAIL, { title: entry.title, reason: String(err) });
      patchEntry(id, { status: 'error', error: 'Upload failed. Please try again.' });
    }
  }, [patchEntry, pollStatus]);

  // ── Queue driver: start next pending file whenever no upload is active ───
  useEffect(() => {
    if (view !== 'queue') return;
    const hasActive = entries.some(
      e => e.status === 'uploading' || e.status === 'completing',
    );
    if (hasActive) return;
    const next = entries.find(e => e.status === 'pending');
    if (next) doUpload(next);
  }, [entries, view, doUpload]);

  // ── Accept multiple files, flag name collisions for resolution ──────────
  const acceptFiles = useCallback((files: File[]) => {
    const supported = files.filter(isSupportedFile);
    const rejected = files.length - supported.length;
    if (rejected > 0) {
      dispatch(
        showToast({
          title: 'Unsupported File Type',
          message: `${rejected} file${rejected !== 1 ? 's' : ''} skipped. Only video (MP4, MOV) and image (JPG, PNG, GIF, WEBP, HEIC) files are supported.`,
        }),
      );
    }

    const existingNames = new Set(entries.map(e => e.file.name.toLowerCase()));
    const unique: File[] = [];
    const duplicates: DuplicateConflict[] = [];
    const empty: File[] = [];

    for (const f of supported) {
      if (f.size === 0) {
        empty.push(f);
      } else if (existingNames.has(f.name.toLowerCase())) {
        duplicates.push({ id: uid(), file: f });
      } else {
        existingNames.add(f.name.toLowerCase());
        unique.push(f);
      }
    }

    if (unique.length > 0) {
      setEntries(prev => [...prev, ...unique.map(makeFileEntry)]);
      setView('meta');
    }
    if (duplicates.length > 0) {
      setDuplicateQueue(prev => [...prev, ...duplicates]);
    }
    if (empty.length > 0) {
      dispatch(
        showToast({
          title: 'Empty file skipped',
          message: empty.length === 1
            ? `<b style="color:var(--color-text)">${empty[0].name}</b> is empty (0 bytes) and cannot be uploaded.`
            : `${empty.length} files are empty (0 bytes) and cannot be uploaded.`,
        }),
      );
    }
  }, [entries, dispatch]);

  // ── Resolve the duplicate-filename conflict at the front of the queue ───
  const resolveDuplicate = useCallback((action: 'keep' | 'replace' | 'skip') => {
    setDuplicateQueue(prev => {
      const [current, ...rest] = prev;
      if (!current) return prev;

      if (action === 'replace') {
        setEntries(es => [
          ...es.filter(e => e.file.name.toLowerCase() !== current.file.name.toLowerCase()),
          makeFileEntry(current.file),
        ]);
        setView('meta');
      } else if (action === 'keep') {
        setEntries(es => {
          const existingNames = new Set(es.map(e => e.file.name.toLowerCase()));
          const newName = uniqueFileName(current.file.name, existingNames);
          return [...es, makeFileEntry(renameFile(current.file, newName))];
        });
        setView('meta');
      }
      // 'skip' — discard the duplicate, nothing to add

      return rest;
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) acceptFiles(files);
  }, [acceptFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      acceptFiles(files);
      e.target.value = ''; // reset so same files can be re-selected
    }
  };

  // ── Start the queue ──────────────────────────────────────────────────────
  const startQueue = useCallback(() => {
    if (entries.length === 0) return;
    dispatch(setUploadInProgress(true));
    setView('queue');
    // The queue-driver effect above picks up the first pending entry automatically.
  }, [entries, dispatch]);

  // ── Cancel all uploads and close ─────────────────────────────────────────
  const handleCancelAll = useCallback(() => {
    const hasActive = entries.some(
      e => e.status === 'uploading' || e.status === 'completing' || e.status === 'processing',
    );
    if (hasActive && !window.confirm('Cancel all uploads? Files still uploading or being analysed will be stopped.')) {
      return;
    }
    abortRefs.current.forEach(c => c.abort());
    pollRefs.current.forEach(t => clearTimeout(t));
    dispatch(setUploadInProgress(false));
    dispatch(closeUploadModal());
  }, [entries, dispatch]);

  // ── Computed stats ───────────────────────────────────────────────────────
  const totalFiles  = entries.length;
  const doneFiles   = entries.filter(e => e.status === 'done').length;
  const overallPct  = totalFiles > 0
    ? Math.round(entries.reduce((sum, e) => sum + e.progress, 0) / totalFiles)
    : 0;
  const currentStepIdx = view === 'drop' ? 0 : view === 'meta' ? 1 : 2;

  // ── Minimized floating widget ────────────────────────────────────────────
  if (minimized && view === 'queue') {
    return (
      <div className={styles.minimizedContainer}>
        <div
          className={styles.minimizedCard}
          onClick={() => setMinimized(false)}
          role="button"
          tabIndex={0}
          aria-label="Expand upload panel"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMinimized(false); }}
        >
          <span className={styles.miniSpinner} aria-hidden="true" />
          <div className={styles.miniInfo}>
            <div className={styles.miniTitle}>
              Uploading {doneFiles} / {totalFiles} files
            </div>
            <div className={styles.miniProgressTrack}>
              <div className={styles.miniProgressFill} style={{ width: overallPct + '%' }} />
            </div>
          </div>
          <button
            className={styles.miniExpandBtn}
            onClick={(e) => { e.stopPropagation(); setMinimized(false); }}
            aria-label="Expand"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M2 8l4-4 4 4" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  const currentDuplicate = duplicateQueue[0];

  // ── Full modal ───────────────────────────────────────────────────────────
  return (
    <>
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget && view !== 'queue') handleClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Upload content"
    >
      <div className={[styles.modal, view !== 'drop' ? styles.modalWide : ''].join(' ')}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div>
              <div className={styles.headerTitle}>
                {view === 'queue'
                  ? 'Uploading ' + doneFiles + ' / ' + totalFiles + ' files'
                  : 'Upload Content'}
              </div>
              <div className={styles.headerSub}>
                {view === 'queue'
                  ? 'AI indexing in progress — you can keep working'
                  : 'Video & documents — indexed with AI'}
              </div>
            </div>
          </div>

          <div className={styles.headerActions}>
            {view === 'queue' && (
              <button
                className={styles.minimizeBtn}
                onClick={() => setMinimized(true)}
                aria-label="Minimise upload panel"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M2 5l5 5 5-5" />
                </svg>
              </button>
            )}
            <button
              className={styles.closeBtn}
              onClick={view === 'queue' ? handleCancelAll : handleClose}
              aria-label={view === 'queue' ? 'Cancel all uploads and close' : 'Close upload modal'}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M1 1l12 12M13 1L1 13" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Step bar ── */}
        <div className={styles.stepBar} aria-label="Upload steps">
          {VIEW_STEPS.map((label, i) => (
            <div key={label} className={styles.stepItem}>
              <div
                className={[
                  styles.stepDot,
                  currentStepIdx === i ? styles.stepDotActive : '',
                  currentStepIdx > i  ? styles.stepDotDone   : '',
                ].join(' ')}
                aria-current={currentStepIdx === i ? 'step' : undefined}
              >
                {currentStepIdx > i ? (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M1.5 5l2.5 2.5 4.5-4.5" />
                  </svg>
                ) : (i + 1)}
              </div>
              <span className={[styles.stepLabel, currentStepIdx === i ? styles.stepLabelActive : ''].join(' ')}>
                {label}
              </span>
              {i < VIEW_STEPS.length - 1 && (
                <div className={[styles.stepConnector, currentStepIdx > i ? styles.stepConnectorDone : ''].join(' ')} />
              )}
            </div>
          ))}
        </div>

        {/* ───────────────────────────────────────────────────────────── */}
        {/* Step 1 — Drop zone                                           */}
        {/* ───────────────────────────────────────────────────────────── */}
        {view === 'drop' && (
          <div className={styles.body}>
            <div
              className={[styles.dropzone, dragOver ? styles.dropzoneActive : ''].join(' ')}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Drop files or click to browse"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                className={styles.fileInput}
                onChange={handleFileInput}
              />
              <div className={[styles.dropIconWrap, dragOver ? styles.dropIconWrapActive : ''].join(' ')}>
                {dragOver ? (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v12M8 11l4 4 4-4" />
                    <path d="M4 19h16" />
                  </svg>
                ) : (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                )}
              </div>
              <div className={styles.dropTitle}>
                {dragOver ? 'Release to add files' : 'Drop files here'}
              </div>
              <div className={styles.dropSub}>or click to browse — select multiple files at once</div>
              <div className={styles.fmtRow}>
                {['MP4', 'MOV', 'JPG', 'PNG', 'GIF', 'WEBP'].map(f => (
                  <span key={f} className={styles.fmtBadge}>{f}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ───────────────────────────────────────────────────────────── */}
        {/* Step 2 — Metadata per file                                   */}
        {/* ───────────────────────────────────────────────────────────── */}
        {view === 'meta' && (
          <div className={styles.body}>
            {/* Hidden input for "+ Add more" */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="video/mp4,video/quicktime,.mov,.MOV,.mp4"
              className={styles.fileInput}
              onChange={handleFileInput}
            />

            <div className={styles.metaHeader}>
              <span className={styles.metaCount}>
                {entries.length} file{entries.length !== 1 ? 's' : ''} selected
              </span>
              <button className={styles.addMoreBtn} onClick={() => fileInputRef.current?.click()}>
                + Add more
              </button>
            </div>

            <div className={styles.metaList}>
              {entries.map((entry) => {
                const ext = getExt(entry.file);
                return (
                  <div key={entry.id} className={styles.metaEntry}>
                    {/* File chip row */}
                    <div className={styles.metaEntryHeader}>
                      <span className={[styles.fileExt, styles[`ext${ext}` as keyof typeof styles] ? styles[`ext${ext}` as keyof typeof styles] : ''].join(' ')}>{ext}</span>
                      <div className={styles.fileInfo}>
                        <div className={styles.fileName}>{entry.file.name}</div>
                        <div className={styles.fileSize}>{formatBytes(entry.file.size)}</div>
                      </div>
                      <button
                        className={styles.removeBtn}
                        onClick={() => setEntries(prev => prev.filter(e => e.id !== entry.id))}
                        aria-label={`Remove ${entry.file.name}`}
                      >
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M1 1l12 12M13 1L1 13" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.metaActions}>
              <Button variant="ghost" size="sm" onClick={() => { setEntries([]); setView('drop'); }}>
                ← Back
              </Button>
              <Button
                variant="gradient"
                size="sm"
                onClick={startQueue}
                disabled={entries.length === 0}
                className={styles.btnGreen}
              >
                Start {entries.length > 1 ? `${entries.length} Uploads` : 'Upload'} →
              </Button>
            </div>
          </div>
        )}

        {/* ───────────────────────────────────────────────────────────── */}
        {/* Step 3 — Upload queue                                        */}
        {/* ───────────────────────────────────────────────────────────── */}
        {view === 'queue' && (
          <div className={styles.body}>
            {/* Overall progress */}
            <div className={styles.queueSummary}>
              <div className={styles.queueProgressTrack}>
                <div className={styles.queueProgressFill} style={{ width: overallPct + '%' }} />
              </div>
              <div className={styles.queueSummaryRow}>
                <span className={styles.queueSummaryLabel}>
                  {doneFiles === totalFiles
                    ? 'All files indexed!'
                    : `${doneFiles} of ${totalFiles} complete`}
                </span>
                <span className={styles.queueSummaryPct}>{overallPct}%</span>
              </div>
            </div>

            {/* Per-file rows */}
            <div className={styles.queueList}>
              {entries.map((entry) => {
                const ext       = getExt(entry.file);
                const isActive  = entry.status === 'uploading' || entry.status === 'completing';
                const isProc    = entry.status === 'processing';
                const isDone    = entry.status === 'done';
                const isError   = entry.status === 'error';
                const isPending = entry.status === 'pending';

                return (
                  <div
                    key={entry.id}
                    className={[
                      styles.queueEntry,
                      isActive ? styles.queueEntryActive    : '',
                      isProc   ? styles.queueEntryProcessing : '',
                      isDone   ? styles.queueEntryDone       : '',
                      isError  ? styles.queueEntryError      : '',
                    ].join(' ')}
                  >
                    {/* File type badge */}
                    <div className={styles.queueEntryLeft}>
                      <span className={styles.queueExt}>{ext}</span>
                    </div>

                    {/* Main content */}
                    <div className={styles.queueEntryMain}>
                      <div className={styles.queueEntryName}>{entry.title || entry.file.name}</div>

                      {/* Per-file mini progress bar */}
                      {(isActive || isProc) && (
                        <div className={styles.queueMiniTrack}>
                          <div className={styles.queueMiniFill} style={{ width: entry.progress + '%' }} />
                        </div>
                      )}

                      {/* Status line */}
                      <div className={styles.queueEntryStatus}>
                        {isPending && (
                          <span className={styles.statusPending}>Queued</span>
                        )}
                        {isActive && (
                          <span className={styles.statusUploading}>
                            {entry.status === 'completing'
                              ? 'Assembling on server...'
                              : Math.round(entry.progress) + '%'}
                          </span>
                        )}
                        {isProc && entry.processingStatus && (
                          <span className={styles.statusProcessing}>
                            {PROCESSING_LABELS[entry.processingStatus]}
                          </span>
                        )}
                        {isDone  && <span className={styles.statusDone}>✓ Indexed</span>}
                        {isError && <span className={styles.statusError}>✗ {entry.error}</span>}
                      </div>
                    </div>

                    {/* Right icon / cancel */}
                    <div className={styles.queueEntryRight}>
                      {isDone && (
                        <span className={styles.doneCheck} aria-label="Done">
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M1.5 6l3 3 6-6" />
                          </svg>
                        </span>
                      )}
                      {isProc && (
                        <span className={styles.entrySpinner} aria-hidden="true" />
                      )}
                      {isError && (
                        <button
                          className={styles.retryEntryBtn}
                          aria-label={`Retry ${entry.title || entry.file.name}`}
                          onClick={() => {
                            startedIds.current.delete(entry.id);
                            trackEvent(ANALYTICS_EVENTS.UPLOAD_RETRY, { title: entry.title });
                            patchEntry(entry.id, { status: 'pending', progress: 0, error: null });
                          }}
                        >
                          Retry
                        </button>
                      )}
                      {(isPending || isActive) && (
                        <button
                          className={styles.cancelEntryBtn}
                          aria-label={`Cancel ${entry.title || entry.file.name}`}
                          onClick={() => {
                            abortRefs.current.get(entry.id)?.abort();
                            startedIds.current.delete(entry.id);
                            setEntries(prev => prev.filter(e => e.id !== entry.id));
                          }}
                        >
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M1 1l8 8M9 1L1 9" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>

    {/* ── Duplicate filename conflict ── */}
    {currentDuplicate && (
      <div className={styles.conflictBackdrop} role="dialog" aria-modal="true" aria-label="Duplicate file name">
        <div className={styles.conflictDialog}>
          <p className={styles.conflictTitle}>Duplicate file name</p>
          <p className={styles.conflictMessage}>
            A file named <strong>{currentDuplicate.file.name}</strong> is already in this upload.
            {duplicateQueue.length > 1 && ` (${duplicateQueue.length - 1} more duplicate${duplicateQueue.length > 2 ? 's' : ''} to resolve)`}
          </p>
          <div className={styles.conflictActions}>
            <button className={styles.conflictBtn} onClick={() => resolveDuplicate('skip')}>
              Skip
            </button>
            <button className={styles.conflictBtn} onClick={() => resolveDuplicate('replace')}>
              Replace
            </button>
            <button className={[styles.conflictBtn, styles.conflictBtnPrimary].join(' ')} onClick={() => resolveDuplicate('keep')}>
              Keep both
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
